import React, { useEffect, useState } from 'react';
import SupervisorLayout from '../../components/SupervisorLayout';
import { fetchSupervisorSeeParameters } from '../../api/supervisor';
import { listProducts } from '../../api/products';
import './SupervisorParameterDetailsView.css';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

// Rendu dynamique (2026-09-17) : remplace les donnees 100% mock d'origine par
// les vraies ProductConfig/ProductConfigPayout du Groupe/Banque choisi, via
// l'endpoint DEDIE /supervisor/see-parameters/ (gate uniquement par 'Voir
// Parametres', decouple expres de '/supervisor/product-configs/' — celui-ci
// exige en plus la permission d'EDITION 'Parametres de loterie', bug trouve
// en test reel qui causait un 403 pour un superviseur n'ayant que "Voir
// Parametres" accorde). Toujours LECTURE SEULE (pas de formulaire ici).
const SupervisorParameterDetailsView = ({ lottery, lotteryName, groupId, groupName, bankId, bankName, onBack }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([
      listProducts(),
      fetchSupervisorSeeParameters({ tirage: lottery, group: groupId, bank: bankId }),
    ])
      .then(([products, configs]) => {
        if (cancelled) return;
        const configByProduct = Object.fromEntries(configs.map((c) => [c.product, c]));
        setRows(products.map((p) => {
          const cfg = configByProduct[p.id];
          return {
            productId: p.id,
            name: p.name,
            pointsPrice: cfg ? cfg.points_price : null,
            onlineLimit: cfg ? cfg.online_limit : null,
            localLimit: cfg ? cfg.local_limit : null,
            salesCommission: cfg ? cfg.sales_commission : null,
            savingsPct: cfg ? cfg.savings_pct : null,
            hasConfig: Boolean(cfg),
            keys: p.payout_keys.map((k) => {
              const payout = cfg?.payouts.find((pay) => pay.key === k.id);
              return { keyId: k.id, key: k.key, wonPerPoint: payout ? payout.won_per_point : null };
            }),
          };
        }));
      })
      .catch(() => { if (!cancelled) setError('Impossible de charger les parametres — verifie que le serveur backend tourne.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lottery, groupId, bankId]);

  return (
    <SupervisorLayout>
      <p className="svpdv-back" onClick={onBack}>◀ Back to Search</p>

      <div className="svpdv-card">
        <div className="svpdv-card-header">
          <span className="svpdv-card-title">Detalles De Parametros</span>
        </div>
        <div className="svpdv-card-body">
          <p className="svpdv-line">Lottery: {lottery} - {lotteryName}</p>
          <p className="svpdv-line">{groupId ? `Groupe: ${groupName || groupId}` : `Banque: ${bankName || bankId}`}</p>
          <hr className="svpdv-sep" />

          {error && <p className="svpdv-error">{error}</p>}
          {loading && <p className="svpdv-loading">Chargement...</p>}

          {!loading && !error && rows.map((row) => (
            <div key={row.productId} className="svpdv-group">
              <p className="svpdv-group-name">{row.name}</p>
              {row.hasConfig ? (
                <>
                  <table className="svpdv-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Points Price</th>
                        <th>Online Limit</th>
                        <th>Local Limit</th>
                        <th>Sales Comission</th>
                        <th>Savings %</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{row.name}</td>
                        <td>{fmt(row.pointsPrice)}</td>
                        <td>{fmt(row.onlineLimit)}</td>
                        <td>{fmt(row.localLimit)}</td>
                        <td>{row.salesCommission}</td>
                        <td>{row.savingsPct}</td>
                      </tr>
                    </tbody>
                  </table>

                  <table className="svpdv-key-table">
                    <thead>
                      <tr>
                        <th>Key</th>
                        <th>Won per Point</th>
                      </tr>
                    </thead>
                    <tbody>
                      {row.keys.map((k) => (
                        <tr key={k.keyId}>
                          <td>{k.key}</td>
                          <td>{k.wonPerPoint === null ? '—' : fmt(k.wonPerPoint)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <p className="svpdv-no-config">Aucune configuration pour ce produit sur cette loterie.</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </SupervisorLayout>
  );
};

export default SupervisorParameterDetailsView;
